// INSPIRAȚIA, lângă machetă (Cătălin, 7.10.2026): imaginile după care agentul
// a generat fațada soluției, numerotate ca în tava omului (1–3), și ce a luat
// din fiecare — dorințele din descriere care numesc imaginea, cu soarta lor.
//
// Sursa e CARTEA FAȚADEI de pe soluție (`summary.fatada`, scrisă de agent):
// referințele cu care s-a generat, nu tava de acum. O imagine din catalog se
// arată după id; o poză a omului stă în temă (data URL), deci se caută acolo
// după nume — dacă omul a scos-o între timp din tavă, rămâne numele ei.
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useT } from "../../i18n";
import { citesteLocalizare, srcPozaFatada } from "../../pages/dosarclient";
import { IMAGINI, imagineMare, imagineMica } from "../../pages/fatada/catalog";

// o soluție mai veche poate cita o imagine scoasă între timp din catalog
const DIN_CATALOG = new Set(IMAGINI.map((i) => i.id));

export type ReferintaCarte = { nr?: number; sursa: "catalog" | "incarcata"; id?: string | null;
                               stil?: string | null; nume?: string | null; poza?: string | null };
export type DorintaCarte = { wish: string; reference?: number; fate: "kept" | "adapted" | "impossible";
                             how?: string };
export type CarteFatada = {
  referinte?: ReferintaCarte[];
  standard?: { nivel?: string; eticheta?: string; sistem?: string } | null;
  analiza?: { from_brief?: { wishes?: DorintaCarte[] } } | null;
};

/** Cartea fațadei din rezumatul unei soluții, dacă are referințe. */
export function carteaDin(summary: Record<string, unknown> | undefined | null): CarteFatada | null {
  const c = summary?.fatada as CarteFatada | undefined;
  return c && Array.isArray(c.referinte) && c.referinte.length ? c : null;
}

export default function Inspiratie({ projectId, carte }: { projectId: string; carte: CarteFatada }) {
  const t = useT();
  const tk = (k: string, v?: Record<string, string | number>) => t(k as never, v);
  const [poze, setPoze] = useState<Record<string, string>>({});
  const [mare, setMare] = useState<number | null>(null);
  const refs = (carte.referinte ?? []).map((r, i) => ({ ...r, nr: r.nr ?? i + 1 }));
  const dorinte = carte.analiza?.from_brief?.wishes ?? [];
  // pozele cu id în carte (de la 7.10.2026) se arată direct; numai cele vechi
  // se mai caută în temă, după nume
  const areIncarcate = refs.some((r) => r.sursa === "incarcata" && !r.poza);

  // pozele omului: din temă, după nume — numai dacă soluția are vreuna
  useEffect(() => {
    if (!areIncarcate) return;
    let viu = true;
    citesteLocalizare(projectId).then((l) => {
      if (!viu) return;
      const m: Record<string, string> = {};
      for (const r of l?.tema?.fatada?.referinte ?? []) {
        const src = r.sursa === "incarcata" ? srcPozaFatada(projectId, r) : null;
        if (r.sursa === "incarcata" && src) m[r.nume] = src;
      }
      setPoze(m);
    }).catch(() => undefined);
    return () => { viu = false; };
  }, [projectId, areIncarcate]);

  useEffect(() => {
    if (mare == null) return;
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") setMare(null); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [mare]);

  const sursa = (r: (typeof refs)[number], mareImg = false): string | null =>
    r.sursa === "catalog" ? (r.id && DIN_CATALOG.has(r.id) ? (mareImg ? imagineMare(r.id) : imagineMica(r.id)) : null)
      : r.poza ? srcPozaFatada(projectId, { poza: r.poza })
      : r.nume ? poze[r.nume] ?? null : null;
  const aleEi = (nr: number) => dorinte.filter((d) => d.reference === nr);
  const generale = dorinte.filter((d) => d.reference == null);
  const deschisa = mare == null ? null : refs.find((r) => r.nr === mare) ?? null;

  const Dorinte = ({ lista }: { lista: DorintaCarte[] }) => (
    <ul className="insp-dorinte">
      {lista.map((d, i) => (
        <li key={i} className={d.fate}>
          <span className="chip">{tk(`fatada.insp.soarta.${d.fate}`)}</span>
          <span>{d.wish}{d.how && d.fate !== "kept" ? ` — ${d.how}` : ""}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <details className="fatada-inspiratie" open>
      <summary>
        {t("fatada.insp.titlu" as never)}
        {carte.standard?.eticheta && <span className="lbl"> · {carte.standard.eticheta}</span>}
      </summary>
      <div className="insp-tava">
        {refs.map((r) => {
          const src = sursa(r);
          return (
            <button key={r.nr} className="insp-slot" onClick={() => setMare(r.nr)}
                    title={r.sursa === "incarcata" ? r.nume ?? "" : r.stil ?? ""}>
              {src ? <img src={src} alt="" />
                : <span className="insp-lipsa">{r.sursa === "catalog" ? tk("fatada.insp.scoasa") : r.nume ?? "?"}</span>}
              <span className="insp-nr">{r.nr}</span>
              {aleEi(r.nr).length > 0 && <span className="insp-are">{aleEi(r.nr).length}</span>}
            </button>
          );
        })}
      </div>
      {generale.length > 0 && <Dorinte lista={generale} />}

      {/* mărirea, prin portal în `.ob` (ca asistentul): panoul derulează și
          plutește, iar o cutie fixă în el s-ar tăia după marginile lui */}
      {deschisa && createPortal(
        <div className="ob" style={{ minHeight: 0, height: 0, background: "none" }}>
        <div className="insp-lupa" role="dialog" onClick={() => setMare(null)}>
          <figure onClick={(e) => e.stopPropagation()}>
            {sursa(deschisa, true)
              ? <img src={sursa(deschisa, true) ?? ""} alt="" />
              : <p className="note">{deschisa.sursa === "catalog" ? tk("fatada.insp.scoasa")
                                     : tk("fatada.insp.poza_scoasa", { nume: deschisa.nume ?? "" })}</p>}
            <figcaption>
              <b>{tk("fatada.insp.imaginea", { nr: deschisa.nr })}</b>
              {deschisa.sursa === "incarcata" ? ` · ${deschisa.nume ?? ""}` : ""}
              {aleEi(deschisa.nr).length > 0
                ? <Dorinte lista={aleEi(deschisa.nr)} />
                : <p className="note">{t("fatada.insp.fara_dorinte" as never)}</p>}
            </figcaption>
            <div className="insp-nav">
              {refs.length > 1 && refs.map((r) => (
                <button key={r.nr} className={r.nr === deschisa.nr ? "on" : ""}
                        onClick={() => setMare(r.nr)}>{r.nr}</button>
              ))}
              <button onClick={() => setMare(null)} aria-label={t("fatada.insp.inchide" as never)}>✕</button>
            </div>
          </figure>
        </div>
        </div>,
        document.body)}
    </details>
  );
}
